import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatToolbar } from '@angular/material/toolbar'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router'
import {
  ElderFormFieldDenseDirective,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderShellSlotDirective,
  ElderThemeDirective,
  ElderTruncatePipe,
  Interval,
  IsoIntervalPipe,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Instant, ZoneId } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { combineLatestWith, Observable } from 'rxjs'
import { filter, map, startWith, tap } from 'rxjs/operators'
import { truthyPredicate } from '../../../../../functional.util'
import {
  ProductionListCombinedContext,
  ProductionListPreviewService,
} from '../../../production/lists/production-list-preview.service'
import { WorkStation } from '../../../shopfloor/workstations/work-station'
import { TerminalWsConfigurationComponent } from '../../../shopfloor/workstations/work-station-configuration-browser/ws-configuration-input-bar/terminal-ws-configuration.component'
import { ControlTerminal, TerminalWorkCenter } from '../../control-terminal'
import { ControlTerminalMode } from '../../control-terminal-mode'
import { TerminalRoutingService } from '../../terminal-routing.service'

@Component({
  selector: 'app-work-center-terminal',
  templateUrl: './work-center-terminal.component.html',
  styleUrls: ['./work-center-terminal.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatFormField,
    ElderFormFieldDenseDirective,
    MatLabel,
    ElderSelectComponent,
    ElderSelectValueDirective,
    TerminalWsConfigurationComponent,
    MatButton,
    ElderShellSlotDirective,
    NgTemplateOutlet,
    RouterOutlet,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
    IsoIntervalPipe,
    ElderTruncatePipe,
  ],
})
export class WorkCenterTerminalComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly TerminalMode = ControlTerminalMode
  public readonly controlTerminal$ = new Observable<ControlTerminal>()
  public readonly activeWorkCenter$ = new Observable<TerminalWorkCenter>()
  public readonly activeWorkStation$ = new Observable<WorkStation>()
  public readonly mode$ = new Observable<ControlTerminalMode>()
  public readonly interval$ = new Observable<Interval>()
  public readonly currentTime$ = new Observable<Instant>()
  public readonly privileged$: Observable<boolean>
  public readonly includeUnstartable$: Observable<boolean>
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private prodListPreviewService = inject(ProductionListPreviewService)
  private terminalRoutingService = inject(TerminalRoutingService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = this.route
    const parent = this.route.parent
    if (parent) {
      this.controlTerminal$ = parent.data
        .pipe(map((data) => data['controlTerminal']))
        .pipe(filter(truthyPredicate))
        .pipe(
          tap((controlTerminal) => {
            this.log.debug(
              `Loaded control terminal from parent ${controlTerminal?.controlCenterId}`
            )
            if (controlTerminal.workCenters.length === 0) {
              const message = 'Terminal has no work centers configured! Cannot Initialize'
              this.log.error(message)
              throw new Error(message)
            }
          })
        )
      this.log.debug(`got params: ${JSON.stringify(this.route.snapshot.params)}`)
      this.log.debug(`got parent params: ${JSON.stringify(parent.snapshot.params)}`)
    } else {
      this.log.error(`Missing parent route`)
    }
    const activeWorkCenterId$ = this.route.params.pipe(
      map((params) => <string>params['workCenterId'])
    ) // guaranteed to be present

    this.activeWorkCenter$ = this.controlTerminal$
      .pipe(combineLatestWith(activeWorkCenterId$))
      .pipe(
        map(([terminal, workCenterId]) =>
          terminal.workCenters.find((wc) => wc.id === workCenterId)
        ),
        filter(truthyPredicate),
        tap((wc) => console.debug(`Got activeWorkCenter from terminal: ${wc?.id}`))
      )
    const queryParams$ = route.queryParams
    const workStationId$ = queryParams$
      .pipe(map((params) => params['workStationId']))
      .pipe(startWith(null))

    this.activeWorkStation$ = workStationId$
      .pipe(combineLatestWith(this.activeWorkCenter$))
      .pipe(
        map(([wsIdFromUrl, activeWorkCenter]) => {
          const fallBack = activeWorkCenter.workStations[0]
          if (wsIdFromUrl) {
            return activeWorkCenter.workStations.find((ws) => ws.id === wsIdFromUrl) ?? fallBack
          } else {
            return fallBack
          }
        })
      )
      .pipe(
        tap((wc) => {
          console.debug(`Got activeWorkStation from activeWorkCenter: ${wc?.id}`)
          this.terminalRoutingService.updateQueryParams({ workStationId: wc.id })
        })
      )

    this.mode$ = queryParams$
      .pipe(map((params) => params['mode']))
      .pipe(filter(truthyPredicate))
      .pipe(startWith(ControlTerminalMode.LIVE))

    this.interval$ = queryParams$
      .pipe(map((params) => params['terminalInterval']))
      .pipe(filter(truthyPredicate))
      .pipe(map((terminalInterval) => Interval.parse(terminalInterval)))
      .pipe(startWith(Interval.today(ZoneId.systemDefault())))

    this.currentTime$ = queryParams$
      .pipe(map((params) => params['currentTime']))
      .pipe(filter(truthyPredicate))
      .pipe(map((currentTime) => Instant.parse(currentTime)))
      .pipe(startWith(Instant.now()))

    this.includeUnstartable$ = queryParams$
      .pipe(map((params) => params['includeUnstartable']))
      .pipe(filter(truthyPredicate))
      .pipe(map((includeUnstartable) => coerceBooleanProperty(includeUnstartable)))
      .pipe(startWith(false))

    this.privileged$ = queryParams$
      .pipe(map((params) => params['privileged']))
      .pipe(filter(truthyPredicate))
      .pipe(map((privileged) => coerceBooleanProperty(privileged)))
      .pipe(startWith(false))
  }

  /***************************************************************************
   *                                                                         *
   * Event Handlers                                                          *
   *                                                                         *
   **************************************************************************/

  /**
   * Triggered on user input
   */
  public onWorkCenterActive(workCenter: TerminalWorkCenter): void {
    this.navigateToOverview(workCenter)
  }

  /**
   * Triggered on user input
   */
  public onWorkStationActive(ws: WorkStation): void {
    this.terminalRoutingService.updateQueryParams({ workStationId: ws.id })
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public goHome(wc: TerminalWorkCenter | null): void {
    if (wc) {
      this.navigateToOverview(wc)
    } else {
      this.terminalRoutingService.clearQueryParams()
    }
  }

  public switchMode(
    currentMode: ControlTerminalMode,
    terminalInterval: Interval,
    privileged: boolean
  ): void {
    if (privileged) {
      const modeToSwitchTo =
        currentMode === ControlTerminalMode.LIVE
          ? ControlTerminalMode.PREVIEW
          : ControlTerminalMode.LIVE

      this.terminalRoutingService.updateQueryParams({
        mode: modeToSwitchTo,
        terminalInterval,
      })
    }
  }

  public toggleIncludeUnstartable(current: boolean) {
    const includeUnstartable = !current
    this.terminalRoutingService.updateQueryParams({
      includeUnstartable,
    })
  }

  public onPreviewProdList(
    event: Event,
    mode: ControlTerminalMode,
    interval: Interval,
    currentTime: Instant,
    workCenterId: string,
    activeWorkStationId?: string
  ): void {
    this.prodListPreviewService.previewProdList(
      new ProductionListCombinedContext(
        workCenterId,
        interval,
        currentTime,
        mode,
        [],
        activeWorkStationId
      )
    )
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private navigateToOverview(workCenter: TerminalWorkCenter): void {
    this.router.navigate(['../', workCenter.id, 'orders'], {
      relativeTo: this.route,
    })
  }
}
